import { LevelCatalog } from "@highmarch/app";
import { CheckinClaim, CheckinPay, RARITY_IDS, Rarity } from "@highmarch/domain";
import { btnHtml } from "../kit";
import { icon } from "../iconsKit";
import { chestIconUrl } from "../portraits";
import { toast } from "../toast";
import { chestLine, flyReward, rewardHtml } from "./ordersSheet";
import { checkHtml, sheetHeadHtml, wellHtml } from "../components";
import { PaintedIcon, paintedIconUrl } from "../featureIcons";
import { gemIcon } from "./econArt";
import { bindWelcomePack, welcomePackHtml } from "./welcomePackCard";
import "../styles/offerPolish.css";

export interface CheckinCtx {
  readonly catalog: LevelCatalog;
  readonly now: () => number;
  /** Saves and redraws the hub. */
  readonly onChange: () => void;
  readonly parent: HTMLElement;
}

/** How long the claimed slot's tick shows before the card slides away. */
const CLOSE_AFTER_CLAIM_MS = 900;
/** The calendar is drawn a week at a time; each week ends on its milestone (MP-24). */
const DAYS_PER_WEEK = 7;
const SIGIL_ICON: Readonly<Record<string, PaintedIcon>> = { common: "sigilCommon", uncommon: "sigilUncommon", rare: "sigilRare", epic: "sigilEpic", legendary: "sigilLegendary" };
const CAP = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/** The art of a milestone: its chest, or its Fusion Sigil. */
function milestoneArt(pay: CheckinPay): string | null {
  if (pay.chest) return chestIconUrl(RARITY_IDS[pay.chest.rarity]);
  return pay.sigil !== null ? paintedIconUrl(SIGIL_ICON[RARITY_IDS[pay.sigil]] ?? "sigilLegendary") : null;
}

/** The gems a slot pays, as the gem icon and the amount. */
const gemsHtml = (n: number): string => (n > 0 ? `<span class="gm">${gemIcon(15)}${n}</span>` : "");

/** One slot's face: its chest or sigil (and gems), or its gold and speed-up (and gems). */
function slotBody(pay: CheckinPay): string {
  const art = milestoneArt(pay);
  if (art) return `<img decoding="sync" class="${pay.chest ? "sc" : "sg"}" src="${art}" alt="">${pay.chest ? "" : `<b class="mn">${milestoneName(pay)}</b>`}${gemsHtml(pay.gems)}`;
  return `<div class="sr">${rewardHtml(pay.gold, pay.speedUpMinutes, false)}${gemsHtml(pay.gems)}</div>`;
}

/** What a milestone is, in words ("Epic chest", "Legendary sigil"). */
function milestoneName(pay: CheckinPay): string {
  if (pay.chest) return `${CAP(RARITY_IDS[pay.chest.rarity])} chest`;
  return pay.sigil !== null ? `${CAP(RARITY_IDS[pay.sigil])} sigil` : "Gift";
}

/** Banks a claim into the profile: gold, speed-ups, sigils and any chest (gear included). Returns the chest's toast line. */
function bank(catalog: LevelCatalog, c: CheckinClaim): string | null {
  let line: string | null = null;
  for (const pay of [c.pay, c.welcome]) {
    if (!pay) continue;
    catalog.profile.addGold(pay.gold);
    catalog.profile.addSpeedUp(pay.speedUpMinutes);
    if (pay.sigil !== null && pay.sigils > 0) {
      catalog.profile.forge.addSigils(pay.sigil, pay.sigils);
      line = `Day ${c.slot + 1}: ${pay.sigils > 1 ? `${pay.sigils} ` : "a "}${CAP(RARITY_IDS[pay.sigil])} Fusion Sigil${pay.sigils > 1 ? "s" : ""} for the forge`;
    }
    if (pay.chest) { const gear = catalog.bankChest(pay.chest); line = chestLine(`Day ${c.slot + 1} chest`, pay.chest, gear.length); }
  }
  return line;
}

/**
 * The welcome-back bundle; with the Welcome Back pack on sale (MP-05) the two sit side by side as a pair of cards, each
 * its name over one line of what it holds, so the card grows by no more than a line.
 */
function welcomeHtml(gift: CheckinPay, pack: string): string {
  const pays = rewardHtml(gift.gold, gift.speedUpMinutes);
  if (!pack) return `<div class="welcome">${icon("heart", 16)}<b>Welcome-back gift</b><div class="sr">${pays}</div></div>`;
  return `<div class="wback"><div class="welcome"><b>Welcome-back gift</b><div class="sr">${icon("heart", 15)}${pays}</div></div>${pack}</div>`;
}

/**
 * The four weeks' milestones (days 7, 14, 21 and 28) on a navy plate: each one's chest or sigil in its well, its day
 * and gems, a gold bar filling as the days are claimed, the current week's ringed and the reached ones ticked.
 */
function stripHtml(slots: readonly CheckinPay[], got: number, week: number): string {
  const weeks = Math.floor(slots.length / DAYS_PER_WEEK);
  const cells = Array.from({ length: weeks }, (_, w) => {
    const day = (w + 1) * DAYS_PER_WEEK, pay = slots[day - 1], done = got >= day;
    const art = milestoneArt(pay);
    const well = wellHtml({ art: art ?? paintedIconUrl("crate"), size: "sm", rarity: pay.chest ? pay.chest.rarity : (pay.sigil ?? Rarity.Rare), dim: done, label: milestoneName(pay) });
    return `<div class="cms${done ? " got" : w === week ? " now" : ""}">${well}${done ? checkHtml("cmck") : ""}<small>Day ${day}</small>${pay.gems > 0 ? `<b>${gemIcon(13)}${pay.gems}</b>` : `<b>${milestoneName(pay).split(" ")[0]}</b>`}</div>`;
  }).join("");
  const fill = Math.max(0, Math.min(1, (got - DAYS_PER_WEEK) / (slots.length - DAYS_PER_WEEK)));
  return `<div class="cstrip" style="--n:${weeks}"><div class="bar"><i style="width:${(fill * 100).toFixed(1)}%"></i></div>${cells}</div>`;
}

/**
 * The check-in calendar (ENGAGEMENT_2026-09-23 §B5; 28 days since MP-24): one gift per day the game is opened, never
 * reset by a missed day — so the copy never counts a streak or warns about losing one. Drawn a week at a time (this
 * week's seven days, the milestone last and wide) over the four weeks' milestone strip. It comes up by itself once a
 * day on the first hub visit; closing it leaves today's check-in waiting in the Chests tab. After three or more days
 * away the welcome-back bundle rides on the same claim, with the Welcome Back pack beside it while on sale (MP-05).
 */
export function showCheckin(ctx: CheckinCtx): HTMLElement {
  const { catalog } = ctx;
  const ci = catalog.checkin;
  const level = Math.max(1, catalog.highestLevelCleared);
  const sheet = document.createElement("div");
  sheet.className = "sheet panel checkin";
  const today = ci.slot, open = ci.canClaim(ctx.now()), n = ci.slots.length;
  // slots taken this cycle: a cycle just completed today (slot back to 0) still shows them all ticked until tomorrow
  const got = !open && today === 0 && ci.cycleStarted ? n : today;
  // the week on show: today's (or, once claimed, the one today's claim was in)
  const week = Math.floor((open ? today : Math.max(0, got - 1)) / DAYS_PER_WEEK);
  const pays = ci.slots.map((s) => ci.payFor(s, level));
  const cells = pays.slice(week * DAYS_PER_WEEK, (week + 1) * DAYS_PER_WEEK).map((pay, k) => {
    const i = week * DAYS_PER_WEEK + k;
    const state = i < got ? "got" : open && i === today ? "today" : "";
    const big = milestoneArt(pay) !== null;
    return `<div class="cslot ${state}${big ? " big" : ""}"><small>Day ${i + 1}</small>${slotBody(pay)}${state === "got" ? `<i class="tick">${icon("star", 14)}</i>` : ""}</div>`;
  }).join("");
  const welcome = ci.welcomePending ? ci.payFor(catalog.balance.orders.welcomeBack, level) : null;
  const weeks = Math.ceil(n / DAYS_PER_WEEK);
  sheet.innerHTML = `
    ${sheetHeadHtml({ title: welcome ? "Welcome back!" : "Daily check-in", sub: "", portrait: icon("star", 34, "#fff8e6"), tint: "gold" })}
    ${welcome ? welcomeHtml(welcome, welcomePackHtml(catalog, ctx.now())) : ""}
    <div class="cweek"><b>Week ${week + 1} of ${weeks}</b><small>${open ? `Today: day ${today + 1} of ${n}` : `${got} of ${n} days claimed`}</small></div>
    <div class="cgrid">${cells}</div>
    ${weeks > 1 ? stripHtml(pays, got, week) : ""}
    ${open ? btnHtml("CLAIM", "gold", { className: "claimci" }) : `<p class="none">Come back tomorrow for day ${today + 1}.</p>`}`;
  const close = () => sheet.remove();
  sheet.querySelector<HTMLButtonElement>(".close")!.onclick = close;
  bindWelcomePack(sheet);
  sheet.querySelector<HTMLButtonElement>(".claimci")?.addEventListener("click", (e) => {
    const btn = e.currentTarget as HTMLButtonElement;
    const c = ci.claim(ctx.now(), level);
    if (!c) { close(); return; }
    const line = bank(catalog, c);
    ctx.onChange();
    flyReward(btn, c.pay.gold + (c.welcome?.gold ?? 0) + (c.pay.chest?.gold ?? 0));
    if (line) toast(ctx.parent, line);
    sheet.querySelector(".cslot.today")?.classList.replace("today", "got");
    btn.disabled = true;
    window.setTimeout(close, CLOSE_AFTER_CLAIM_MS);
  });
  ctx.parent.append(sheet);
  return sheet;
}
